import { useState } from 'react'
import type { Activity, ActivityLink, TipoLink } from '@/types'
import {
  formatInTimezone,
  getTimezoneAbbr,
  getLocalDate,
  getUserTimezone,
  getUserTimezoneAbbr,
  userMatchesVenueTimezone,
} from '@/lib/timezones'
import { buildMapsUrl } from '@/lib/maps'
import { titleWithEmoji } from '@/lib/activityEmoji'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useActivityAssignments, useOrgMembers } from '@/hooks/useActivities'
import { useOrg } from '@/context/OrgContext'
import {
  Drawer,
  DrawerContent,
} from '@/components/ui/drawer'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarGroup,
} from '@/components/ui/avatar'
import {
  ExternalLink,
  Plane,
  MapPin,
  Hotel,
  FileText,
  Clock,
  Globe,
  Users,
} from 'lucide-react'
import type { ActivityStatus } from '@/lib/activityStatus'
import { formatTimeUntil } from '@/lib/activityStatus'

const LINK_ICONS: Record<TipoLink, React.ReactNode> = {
  boarding_pass: <Plane className="w-4 h-4" />,
  maps: <MapPin className="w-4 h-4" />,
  hotel: <Hotel className="w-4 h-4" />,
  general: <FileText className="w-4 h-4" />,
}

const LINK_LABELS: Record<TipoLink, string> = {
  boarding_pass: 'Boarding pass',
  maps: 'Maps',
  hotel: 'Hotel',
  general: 'Link',
}

interface ActivityDetailDrawerProps {
  activity: Activity
  status?: ActivityStatus
  now?: Date
  children: React.ReactNode
}

export function ActivityDetailDrawer({ activity, status, now, children }: ActivityDetailDrawerProps) {
  const [open, setOpen] = useState(false)
  const isDesktop = useMediaQuery('(min-width: 640px)')

  const content = <DetailContent activity={activity} status={status} now={now} />

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <div onClick={(e) => { e.stopPropagation(); setOpen(true) }} className="cursor-pointer">
          {children}
        </div>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>{activity.titulo}</DialogTitle>
            <DialogDescription>Activity details</DialogDescription>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <div
        onClick={(e) => { e.stopPropagation(); setOpen(true) }}
        className="cursor-pointer"
      >
        {children}
      </div>
      <DrawerContent className="max-h-[88dvh]">
        <div className="overflow-y-auto pb-safe">
          {content}
        </div>
      </DrawerContent>
    </Drawer>
  )
}

function DetailContent({ activity, status, now }: {
  activity: Activity
  status?: ActivityStatus
  now?: Date
}) {
  const startTime = formatInTimezone(activity.start_time_utc, activity.venue_timezone)
  const endTime = activity.end_time_utc
    ? formatInTimezone(activity.end_time_utc, activity.venue_timezone)
    : null
  const tzAbbr = getTimezoneAbbr(activity.venue_timezone)
  const endsNextDay = !!activity.end_time_utc &&
    getLocalDate(activity.end_time_utc, activity.venue_timezone) !==
    getLocalDate(activity.start_time_utc, activity.venue_timezone)

  const showLocalTime = !userMatchesVenueTimezone(activity.venue_timezone)
  const userTz = getUserTimezone()
  const localStartTime = showLocalTime ? formatInTimezone(activity.start_time_utc, userTz) : null
  const localEndTime = showLocalTime && activity.end_time_utc
    ? formatInTimezone(activity.end_time_utc, userTz)
    : null
  const localTzAbbr = showLocalTime ? getUserTimezoneAbbr() : null

  const isLive = status === 'live'
  const isNext = status === 'next'
  const isPast = status === 'past'

  return (
    <div className={cn('px-5 pt-3 pb-6', isPast && 'opacity-80')}>
      {/* Hero: status chip + big title */}
      <div className="detail-hero">
        {isLive && (
          <span className="chip chip-live">
            <span className="dot" /> Live now
          </span>
        )}
        {isNext && now && (
          <span className="chip chip-primary">
            Next · in {formatTimeUntil(activity.start_time_utc, now)}
          </span>
        )}
        <h2 className="title">{titleWithEmoji(activity.titulo)}</h2>
      </div>

      {/* Info rows — time / timezone / venue */}
      <InfoRow icon={<Clock className="w-4 h-4" />} label="Time">
        <span className="mono">
          {startTime}{endTime && ` — ${endTime}`}
        </span>
        {endsNextDay && <span className="text-primary text-xs ml-1">(next day)</span>}
        {showLocalTime && localStartTime && (
          <span className="secondary mono">
            {localStartTime}{localEndTime && ` — ${localEndTime}`} {localTzAbbr} · your time
          </span>
        )}
      </InfoRow>

      <InfoRow icon={<Globe className="w-4 h-4" />} label="Timezone">
        <span className="mono">
          {tzAbbr} · {activity.venue_timezone.replace('America/', '').replace('Pacific/', '')}
        </span>
      </InfoRow>

      {activity.venue_name && (
        <a
          href={buildMapsUrl(activity.venue_name)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="block group/venue"
        >
          <InfoRow icon={<MapPin className="w-4 h-4" />} label="Venue">
            <span className="underline underline-offset-[3px] decoration-[color:var(--border-strong)] group-hover/venue:decoration-primary group-hover/venue:text-primary transition-colors">
              {activity.venue_name}
            </span>
            <ExternalLink className="w-3 h-3 ml-1 inline text-[color:var(--subtle-foreground)] opacity-60" />
          </InfoRow>
        </a>
      )}

      {/* Description */}
      {activity.descripcion && (
        <div className="drawer-section">
          <p className="m-0 text-sm leading-[1.55] text-foreground whitespace-pre-wrap">
            {activity.descripcion}
          </p>
        </div>
      )}

      {/* Crew */}
      <CrewSection activityId={activity.id} />

      {/* Attachments */}
      {activity.activity_links && activity.activity_links.length > 0 && (
        <div className="drawer-section">
          <p className="section-label">Attachments</p>
          <div className="space-y-1.5">
            {activity.activity_links.map(link => (
              <AttachmentRow key={link.id} link={link} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function InfoRow({ icon, label, children }: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="info-row">
      <span className="info-icon">{icon}</span>
      <span className="info-label">{label}</span>
      <span className="info-val">{children}</span>
    </div>
  )
}

function CrewSection({ activityId }: { activityId: string }) {
  const { activeOrg } = useOrg()
  const { data: assignedIds } = useActivityAssignments(activityId)
  const { data: members } = useOrgMembers(activeOrg?.id)

  if (!assignedIds || assignedIds.length === 0) return null

  const assigned = (members ?? []).filter(m => assignedIds.includes(m.user_id))
  if (assigned.length === 0) return null

  return (
    <div className="drawer-section">
      <p className="section-label">
        <Users className="w-3.5 h-3.5" />
        Crew ({assigned.length})
      </p>
      <div className="flex items-center gap-3">
        <AvatarGroup>
          {assigned.slice(0, 5).map(m => (
            <Avatar key={m.user_id} size="sm">
              {m.profile?.avatar_url ? (
                <AvatarImage src={m.profile.avatar_url} alt={m.profile.nombre} />
              ) : null}
              <AvatarFallback>
                {m.profile?.nombre?.charAt(0)?.toUpperCase() ?? '?'}
              </AvatarFallback>
            </Avatar>
          ))}
        </AvatarGroup>
        <span className="text-xs text-muted-foreground">
          {assigned.map(m => m.profile?.nombre?.split(' ')[0] ?? '?').join(', ')}
        </span>
      </div>
    </div>
  )
}

function AttachmentRow({ link }: { link: ActivityLink }) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      className="attachment"
    >
      <span className="att-ic">{LINK_ICONS[link.tipo]}</span>
      <div className="flex-1 min-w-0">
        <div className="att-title truncate">{link.titulo}</div>
        <div className="att-sub">{LINK_LABELS[link.tipo]}</div>
      </div>
      <ExternalLink className="w-4 h-4 text-[color:var(--subtle-foreground)] shrink-0" />
    </a>
  )
}
